import { skipToken } from "@reduxjs/toolkit/query";
import type { PermissionKind } from "permissions/_api/permission-kinds-api-service";
import {
  type FoundPermissionTarget,
  PERMISSION_TARGET_SEARCH_MAXIMUM_LENGTH,
  canSearchTargetsOf,
  useSearchPermissionTargetsQuery,
} from "permissions/_api/permission-targets-api-service";
import { recordDomainOf, recordPermissionsPath } from "permissions/_views/records/record-permissions-path";
import { useEffect, useMemo, useState } from "react";

/** How long typing must pause before the records are searched, as for people. */
export const RECORD_SEARCH_DEBOUNCE_MS = 300;

/** The fewest characters a records search is asked for. */
export const RECORD_SEARCH_MINIMUM_LENGTH = 2;

/** The most records of each kind the search box lists. A narrower search finds the rest. */
export const RECORD_SEARCH_PAGE_SIZE = 3;

/** A record the search found, ready to draw and to open. */
export type RecordSearchResult = FoundPermissionTarget &
  Readonly<{
    /** The record page that lists who holds a grant on it. */
    path: string;
    kind: PermissionKind;
  }>;

export type RecordSearch = Readonly<{
  results: readonly RecordSearchResult[];
  isFetching: boolean;
  /** Set when one or more kinds could not be searched. The others' results are still listed. */
  isPartial: boolean;
}>;

/** The per-record kinds the records search can find and the record page can open, in the kinds endpoint's order. */
export function searchableRecordKinds(kinds: readonly PermissionKind[]): PermissionKind[] {
  return kinds.filter(
    (kind) => kind.perRecord && !kind.retired && canSearchTargetsOf(kind.resourceType) && recordDomainOf(kind.resourceType) !== undefined,
  );
}

/** Whether `text` is something to search records for. */
export function shouldSearchRecords(text: string): boolean {
  const trimmed = text.trim();
  return trimmed.length >= RECORD_SEARCH_MINIMUM_LENGTH && trimmed.length <= PERMISSION_TARGET_SEARCH_MAXIMUM_LENGTH;
}

/**
 * The Records group of the permissions search: records whose names contain the text, found through
 * each centre's admin-gated permission-targets search (#6174), so an administrator finds a record they
 * cannot open. Each kind answers its first {@link RECORD_SEARCH_PAGE_SIZE}.
 *
 * Only a viewer who may read member permissions searches records; the search answers anyone else 403.
 */
export default function useRecordSearch(text: string, kinds: readonly PermissionKind[], enabled: boolean): RecordSearch {
  const trimmed = text.trim();
  const [debounced, setDebounced] = useState("");
  useEffect(() => {
    const timeoutId = globalThis.setTimeout(() => setDebounced(trimmed), RECORD_SEARCH_DEBOUNCE_MS);
    return () => globalThis.clearTimeout(timeoutId);
  }, [trimmed]);

  // Memoized on purpose: the kinds reach RTK Query's arguments, and a fresh array each render would
  // be a fresh request.
  const searchable = useMemo(() => searchableRecordKinds(kinds), [kinds]);
  const ready = enabled && searchable.length > 0 && shouldSearchRecords(trimmed) && debounced === trimmed;
  const args = useMemo(
    () => (ready ? { resourceTypes: searchable.map((kind) => kind.resourceType), query: debounced, pageSize: RECORD_SEARCH_PAGE_SIZE } : skipToken),
    [ready, searchable, debounced],
  );
  const answer = useSearchPermissionTargetsQuery(args);
  // This search's answer only: `data` keeps the previous search's records while this one is in
  // flight, and they would be offered, and opened, under the new text (#6503 review).
  const current = answer.currentData;

  const results = useMemo(() => {
    if (!ready || !current) return [];
    return current.targets.flatMap((target) => {
      const kind = searchable.find((candidate) => candidate.resourceType === target.resourceType);
      const domain = recordDomainOf(target.resourceType);
      if (!kind || !domain) return [];
      return [{ ...target, kind, path: recordPermissionsPath(domain, target.id) }];
    });
  }, [ready, current, searchable]);

  return {
    results,
    isFetching: ready && answer.isFetching,
    isPartial: ready && (answer.isError || (current?.failed.length ?? 0) > 0),
  };
}
